import React, { useEffect } from "react";

import {Loginpage} from "./pages/LoginPage/LoginPage";
import {PricingListPage} from "./pages/PricingListPage/PricingListPage";
import {DealPage} from "./pages/DealPage/DealPage";
import {AuthContextProvider, useAuthContext} from "./contexts/AuthContext";
import {Outlet} from "react-router-dom";
import {useLocation} from "react-router-dom";
import {Navigate} from "react-router";
import {HomePage} from "./pages/HomePage/HomePage";
import {ProfilePage} from "./pages/ProfilePage/ProfilePage";
import {UserContextProvider, useUserContext} from "./contexts/UserContext";
import {DealContextProvider} from "./contexts/DealContext";
import {HTML5Backend} from "react-dnd-html5-backend";
import {DndProvider} from "react-dnd";
import {ListDealsPage} from "./pages/ListDealsPage/ListDealsPage";
import {ErrorBoundaryLogger} from "./components/ErrorBoundaryLogger";
import {ToastContainer} from "react-toastify";
import {UIContextProvider} from "./contexts/UIContext";
import {CalculatePage} from "./pages/CalculatePage/CalculatePage";
import {CalcContextProvider} from "./contexts/CalcContext";
import {toTitleCase} from "./utils/utils";
import {ForbiddenPage} from "./pages/ForbiddenPage/ForbiddenPage";
import {NotFoundPage} from "./pages/NotFoundPage/NotFoundPage";
import {LeadsPage} from "./pages/CRM/LeadsPage/LeadsPage";
import {CRMPage} from "./pages/CRM/CRMPage";
import {CrmLayout} from "./pages/CrmNext/CrmLayout";
import {PipelinePage} from "./pages/CrmNext/PipelinePage/PipelinePage";
import {PeoplePage as CrmPeoplePage} from "./pages/CrmNext/PeoplePage/PeoplePage";
import {PersonDetailPage as CrmPersonDetailPage} from "./pages/CrmNext/PersonDetailPage/PersonDetailPage";
import {HouseholdDetailPage as CrmHouseholdDetailPage} from "./pages/CrmNext/HouseholdPage/HouseholdDetailPage";
import {ReportsPage as CrmReportsPage} from "./pages/CrmNext/ReportsPage/ReportsPage";
import {SettingsPage as CrmSettingsPage} from "./pages/CrmNext/SettingsPage/SettingsPage";
import {LandingPage} from "./pages/LandingPage/LandingPage";
import {ClientsPage} from "./pages/CRM/ClientsPage/ClientsPage";
import {DefaulSettingsPage} from "./pages/DefaultSettingsPage/DefaulSettingsPage";
import {DefaultQuoteSettingsProvider} from "./contexts/DefaultQuoteSettingsContext";
import ListingsPage from "./pages/ListingsPage/ListingsPage";
import {NotificationsPage} from "./pages/Notifications/NotificationsPage";
import {ListingDetailPage} from "./pages/ListingsPage/ListingDetail/ListingDetailPage";


const DocumentTitleProvider = ({ children }) => {
    let location = useLocation();
    useEffect(function updateDocumentTitle(){
        // The path carries a reset token; never put it in the title.
        if (location.pathname.startsWith('/reset-password')) {
            document.title = 'Reset Password - TrueDesking'
            return
        }

        let suffix = toTitleCase(location.pathname.split('/').join(" "))

        if (suffix === ' ') {
            suffix = 'Desking for brokers, by brokers'
        }

        suffix = suffix.split("-").join(" ")

        document.title = `${suffix} - TrueDesking`
    }, [location]);

    return children
}

const PrivateRoute = ({ children }) => {
    const { isAuthenticated } = useAuthContext();
    const location = useLocation();

    return isAuthenticated ? (
        children
    ) : (
        <Navigate to="/login" state={{ from: location }} replace />
    );
}

const FeatureFlagRoute = ({ name, children }) => {
    const location = useLocation();
    const { isFeatureFlagEnabled } = useUserContext()

    return isFeatureFlagEnabled(name) ? (
        children
    ) : (
        <Navigate to="/calculate" state={{ from: location }} replace />
    );
}

// Roles that may price deals. The nav hides both of these entries already, so
// reaching one means a typed URL, a bookmark kept after a role change, or a
// shared link. /quotes is the fallback rather than /calculate (what the other
// guards use) for the obvious reason — and because the index route points at
// /calculate, this guard is also what lands a no-desking user somewhere usable
// when they open the app.
const DeskingRoute = ({ children }) => {
    const location = useLocation();
    const { canDesk } = useUserContext()

    return canDesk() ? (
        children
    ) : (
        <Navigate to="/quotes" state={{ from: location }} replace />
    );
}

// Account admins only. CrmLayout already hides the nav entry, so reaching one of
// these paths means a typed URL, a bookmark kept after a demotion, or a shared
// link — send them to the section they can use rather than a page whose every
// control the backend would refuse.
const AccountAdminRoute = ({ children }) => {
    const location = useLocation();
    const { isAccountAdmin } = useUserContext()

    return isAccountAdmin() ? (
        children
    ) : (
        <Navigate to="/crm/pipeline" state={{ from: location }} replace />
    );
}

export const routes = [
    {
        element: (
            <React.Fragment>
                <DocumentTitleProvider>
                    <UIContextProvider>
                        <ToastContainer
                            autoClose={2500}
                        />
                        <ErrorBoundaryLogger>
                            <DndProvider backend={HTML5Backend}>
                                    <AuthContextProvider>
                                        <Outlet/>
                                    </AuthContextProvider>
                            </DndProvider>
                        </ErrorBoundaryLogger>
                    </UIContextProvider>
                </DocumentTitleProvider>
            </React.Fragment>
        ),
        children: [
            {
                index: true,
                element: <LandingPage />
            },
            {
                element: ( // THE ORDER OF THESE CONTEXTS MATTER!!!!
                    <PrivateRoute>
                        <UserContextProvider>
                            <DefaultQuoteSettingsProvider>
                                <CalcContextProvider>
                                    <HomePage/>
                                </CalcContextProvider>
                            </DefaultQuoteSettingsProvider>
                        </UserContextProvider>
                    </PrivateRoute>
                ),
                path: "/",
                children: [
                    { index: true, element: <Navigate to="/calculate" replace /> },
                    {
                        path: "/calculate",
                        element: <DeskingRoute><CalculatePage/></DeskingRoute>,
                    },
                    {
                        path: "/quotes",
                        element: <ListDealsPage/>,
                    },
                    {
                        path: "/listings",
                        element: <FeatureFlagRoute name={'listings'}><ListingsPage/></FeatureFlagRoute>,
                    },
                    {
                        path: "/listings/:id",
                        element: <FeatureFlagRoute name={'listings'}><ListingDetailPage/></FeatureFlagRoute>,
                    },
                    {
                        path: "/pricing/:make",
                        element: <DeskingRoute><PricingListPage/></DeskingRoute>,
                    },
                    {
                        path: "/quotes/:id",
                        element: <DealContextProvider><DealPage isDealPage={true}/></DealContextProvider>,
                    },
                    {
                        path: "/account/profile",
                        element: <ProfilePage/>
                    },
                    {
                        path: "/notifications",
                        element: <FeatureFlagRoute name={'notifications'}><NotificationsPage/></FeatureFlagRoute>,
                    },
                    { path: '/deals/*', element: <Navigate to="/quotes" replace /> },
                    {
                        element: <FeatureFlagRoute name={'pipeline'}><CrmLayout/></FeatureFlagRoute>,
                        children: [
                            {
                                path: "/crm/pipeline",
                                element: <PipelinePage/>,
                            },
                            {
                                path: "/crm/people",
                                element: <CrmPeoplePage/>,
                            },
                            {
                                path: "/crm/people/:id",
                                element: <CrmPersonDetailPage/>,
                            },
                            {
                                // Under People, so the People tab stays lit. A static
                                // segment outranks /crm/people/:id.
                                path: "/crm/people/households/:id",
                                element: <CrmHouseholdDetailPage/>,
                            },
                            { path: "/crm/clients", element: <Navigate to="/crm/people" replace /> },
                            { path: "/crm/contacts", element: <Navigate to="/crm/people" replace /> },
                            {
                                path: "/crm/reports",
                                element: <CrmReportsPage/>,
                            },
                            {
                                path: "/crm/settings",
                                element: <AccountAdminRoute><CrmSettingsPage/></AccountAdminRoute>,
                            },
                        ],
                    },
                    {
                        element: (
                            <FeatureFlagRoute name={'crm'}>
                                <CRMPage/>
                            </FeatureFlagRoute>
                        ),
                        children: [
                            {
                                path: "/clients",
                                element: <ClientsPage/>
                            },
                            {
                                path: "/leads",
                                element: <LeadsPage/>
                            }
                        ]
                    },
                    {
                        // Defaults for pricing a deal — fees, taxes, acquisition
                        // and registration fee handling. All of it only takes
                        // effect through a scan, so it is nothing to a role that
                        // cannot run one.
                        path: '/quote-settings/*',
                        element: <DeskingRoute><DefaulSettingsPage/></DeskingRoute>
                    },
                ]
            },
            {
                path: '/login',
                element: <Loginpage/>
            },
            {
                path: '/reset-password/:uid/:token',
                element: <Loginpage/>
            },
            {
                path: '/403',
                element: <ForbiddenPage/>
            },
            {
                path: "/*",
                element: <NotFoundPage />
            },
            {
                path: '/404',
                element: <NotFoundPage/>
            }
        ]
    },
]